import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import emailValidate from '@vr/devkit/validators/emailValidate';
import { useDispatch, useSelector } from 'react-redux';
import { Button, TextField } from '../../elements';
import devices from '../../styles/devices';
import { GPlay, AppStore } from '~/assets';
import {
  ContainerButton,
  Description,
  Actions,
  InputContainer,
} from './common';
import getErrorMsg from '../../helpers/errorMessages';
import { ReCaptcha } from '~/components';
import { setSuccessKey } from '~/store/modules/recaptcha/actions';
import { DYNATRACE_SESSION_KEY } from '~/config/constants';

const ContainerApps = styled.div`
  display: block;

  ${({ onlyMobile }) =>
    onlyMobile &&
    css`
      @media (min-width: ${devices.mobile}) {
        display: none;
      }
    `};
`;
const MobileApps = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px auto 22px;
  max-width: 315px;
`;
const Image = styled.img`
  width: 100%;
  max-width: 148px;
`;
const ContainerReCaptcha = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0;
`;

export default function LoginForm({ onSubmit, resetCaptcha: reset = false }) {
  const { handleSubmit, errors, formState, ...formControl } = useForm({
    mode: 'onChange',
  });
  const history = useHistory();
  const btnStyle = useSelector(state => state?.issuer?.button?.primary?.style);
  const dispatch = useDispatch();
  const [resetRecaptcha, setResetRecaptcha] = useState(false);
  const { siteKey, successKey } = useSelector(state => state.recaptcha);
  const issuer = useSelector(state => state.issuer);

  const useRecaptcha = sessionStorage.getItem(DYNATRACE_SESSION_KEY);

  useEffect(() => {
    setResetRecaptcha(reset);
    if (reset) {
      dispatch(setSuccessKey(null));
    }
  }, [reset]);

  useEffect(() => {
    if (successKey === '') {
      setResetRecaptcha(true);
    }
  }, [successKey]);

  function handleSuccessKey(key) {
    setResetRecaptcha(false);
    dispatch(setSuccessKey(key));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <TextField
        ariaLabel="Email"
        fullWidth
        label="Seu e-mail cadastrado *"
        name="email"
        type="email"
        rules={{
          required: true,
          validate: val => {
            return emailValidate(val);
          },
        }}
        formControl={formControl}
        error={!!errors.email}
        caption={getErrorMsg(errors, 'email')}
      />
      <InputContainer>
        <TextField
          ariaLabel="Senha"
          fullWidth
          label="Sua senha *"
          name="password"
          rules={{
            required: true,
          }}
          type="password"
          formControl={formControl}
          error={!!errors.password}
          caption={getErrorMsg(errors, 'password')}
        />
      </InputContainer>
      {!useRecaptcha && (
        <InputContainer>
          {siteKey && (
            <ContainerReCaptcha>
              <ReCaptcha
                data-testid="captcha"
                sitekey={siteKey}
                onChange={handleSuccessKey}
                resetRecaptcha={resetRecaptcha}
              />
            </ContainerReCaptcha>
          )}
        </InputContainer>
      )}
      <Actions>
        <ContainerButton>
          {!useRecaptcha ? (
            <Button
              color={btnStyle}
              ariaLabel="Fazer login"
              label="Fazer login"
              type="submit"
              variant="contained"
              disabled={!formState.isValid || !successKey}
            />
          ) : (
            <Button
              color={btnStyle}
              ariaLabel="Fazer login"
              label="Fazer login"
              type="submit"
              variant="contained"
              disabled={!formState.isValid}
            />
          )}
        </ContainerButton>
        <ContainerButton>
          <Button
            ariaLabel="Esqueci minha senha"
            label="Esqueci minha senha"
            onClick={() => history.push('/esqueci-minha-senha')}
            variant="transparent"
          />
        </ContainerButton>
        <ContainerButton>
          <Button
            ariaLabel="Esqueci meu e-mail"
            label="Esqueci meu e-mail"
            onClick={() => history.push('/esqueci-meu-email')}
            variant="transparent"
          />
        </ContainerButton>
        <ContainerButton>
          <Button
            color={btnStyle}
            ariaLabel="Ainda não tenho cadastro"
            onClick={() => history.push('/cadastro')}
            label="Ainda não tenho cadastro"
            type="submit"
            variant="contained"
          />
        </ContainerButton>
      </Actions>
      <ContainerApps onlyMobile>
        <Description fontSize="12" color="white">
          Para uma melhor experiência baixe o Aplicativo:
        </Description>
        <MobileApps>
          <a
            href={issuer?.app?.android}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image src={GPlay} alt="Disponível no Google Play" />
          </a>
          <a href={issuer?.app?.ios} target="_blank" rel="noopener noreferrer">
            <Image src={AppStore} alt="Baixar na Apple Store" />
          </a>
        </MobileApps>
      </ContainerApps>
    </form>
  );
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func,
  resetCaptcha: PropTypes.bool,
};
